import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import { beginApiTiming, endApiTiming } from '../utils/performanceDiagnostics';
import { API_BASE_URL } from '../config/apiBase';

const BASE_URL = API_BASE_URL;

let refreshPromise = null;

// Impersonation support — set by ImpersonationContext
let _impersonatedMspId = null;
export function setImpersonatedMspId(mspId) { _impersonatedMspId = mspId; }
export function getImpersonatedMspId() { return _impersonatedMspId; }

export class ApiError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status ?? null;
    this.method = options.method ?? null;
    this.path = options.path ?? null;
    this.requestId = options.requestId ?? null;
    this.code = options.code ?? null;
    this.body = options.body ?? null;
    this.details = options.details ?? null;
  }
}

async function getToken() {
  if (!authService.isAuthenticated()) {
    throw new Error('Not authenticated');
  }
  if (authService.isTokenExpiringSoon()) {
    if (!refreshPromise) {
      refreshPromise = authService.refresh().finally(() => { refreshPromise = null; });
    }
    return refreshPromise;
  }
  return authService.getAccessToken();
}

async function parseResponseBody(res) {
  const text = await res.text();
  if (!text || !text.trim()) return null;

  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch {
      return { parseError: true, rawText: text.trim().slice(0, 1000) };
    }
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function getResponseRequestId(res) {
  return res.headers.get('x-request-id') ||
    res.headers.get('x-correlation-id') ||
    res.headers.get('request-id') ||
    null;
}

function getErrorInfo(body) {
  if (!body) return {};
  if (typeof body === 'string') return { message: body };
  if (body.parseError) return { message: 'The API returned malformed JSON.', details: body.rawText };

  const error = body.error;
  if (typeof error === 'string') return { message: error, requestId: body.requestId };
  if (error && typeof error === 'object') {
    return {
      message: error.message || error.detail || error.title || body.message || body.detail || body.title,
      code: error.code || body.code,
      requestId: error.requestId || body.requestId,
      details: error.details || body.details,
    };
  }

  return {
    message: body.message || body.detail || body.title,
    code: body.code,
    requestId: body.requestId,
    details: body.details,
  };
}

async function buildApiError(res, method, path) {
  const body = await parseResponseBody(res);
  const info = getErrorInfo(body);
  const requestId = getResponseRequestId(res) || info.requestId;
  const statusText = res.statusText ? ` ${res.statusText}` : '';
  const fallback = `${method} ${path} failed with ${res.status}${statusText}`;
  const messageBase = info.message || fallback;
  const message = requestId && !messageBase.includes(requestId)
    ? `${messageBase} (request ${requestId})`
    : messageBase;

  return new ApiError(message, {
    status: res.status,
    method,
    path,
    requestId,
    code: info.code,
    body,
    details: info.details,
  });
}

export function formatApiError(error, fallback = 'Request failed') {
  if (!error) return fallback;
  if (typeof error === 'string') return error;

  const message = error.message || fallback;
  if (error.requestId && !message.includes(error.requestId)) {
    return `${message} (request ${error.requestId})`;
  }

  return message;
}

async function sendRequest(getTokenFn, method, path, body) {
  const timing = beginApiTiming(method, path);
  const isFormData = body instanceof FormData;
  let headers = isFormData ? {} : { 'Content-Type': 'application/json' };
  try {
    const token = await getTokenFn();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch (err) {
    endApiTiming(timing, { ok: false, error: err });
    console.error('Failed to acquire token:', err);
    window.location.href = '/login';
    return;
  }
  if (_impersonatedMspId) {
    headers['X-Act-As-Msp'] = _impersonatedMspId;
  }

  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: isFormData ? body : (body !== undefined && body !== null ? JSON.stringify(body) : undefined),
    });
  } catch (err) {
    endApiTiming(timing, { ok: false, error: err });
    throw new ApiError('Unable to reach Sentinel Command API. Check connectivity and try again.', {
      method,
      path,
      details: err?.message,
    });
  }

  if (res.status === 401) {
    endApiTiming(timing, { ok: false, status: res.status, error: { name: 'Unauthorized' } });
    authService.clearTokens();
    window.location.href = '/login';
    return;
  }

  // MFA enforcement: backend returns 403 with code=MFA_REQUIRED when TOTP not set up
  if (res.status === 403) {
    try {
      const body = await parseResponseBody(res.clone());
      if (body?.code === 'MFA_REQUIRED') {
        endApiTiming(timing, { ok: false, status: res.status, error: { code: 'MFA_REQUIRED' } });
        window.location.href = '/mfa-setup';
        return;
      }
    } catch { /* not JSON, fall through to normal error */ }
  }

  if (!res.ok) {
    const apiError = await buildApiError(res, method, path);
    endApiTiming(timing, { ok: false, status: res.status, error: apiError });
    throw apiError;
  }

  if (res.status === 204) {
    endApiTiming(timing, { ok: true, status: res.status });
    return null;
  }
  const parsed = await parseResponseBody(res);
  endApiTiming(timing, { ok: true, status: res.status });
  return parsed;
}

async function request(method, path, body) {
  return sendRequest(getToken, method, path, body);
}

// Standalone functions for use outside React components
export const apiGet = (path) => request('GET', path);
export const apiPost = (path, body) => request('POST', path, body);
export const apiPut = (path, body) => request('PUT', path, body);
export const apiPatch = (path, body) => request('PATCH', path, body);
export const apiDelete = (path) => request('DELETE', path);

// Hook-based API client (keeps existing consumer pattern)
export function createApiClient(getAccessToken) {
  return {
    get: (path) => sendRequest(getAccessToken, 'GET', path),
    post: (path, body) => sendRequest(getAccessToken, 'POST', path, body),
    put: (path, body) => sendRequest(getAccessToken, 'PUT', path, body),
    patch: (path, body) => sendRequest(getAccessToken, 'PATCH', path, body),
    delete: (path) => sendRequest(getAccessToken, 'DELETE', path),
  };
}

export function useApiClient() {
  const { getAccessToken } = useAuth();
  return useMemo(() => createApiClient(getAccessToken), [getAccessToken]);
}
